'use strict';

// This port must always match the port name defined in `elm/shared/src/PlanHat/PlanHat.elm`
const planHatTrackingPort = 'out_PlanHat_sendPlanHatTracking';

/**
 * Register logger for elm errors
 *
 * NOTICE: We use an indirect reference to window.PinMeToLogger because
 * when the parent scope is initialized (the iife), it has not yet been registered
 * on window.
 *
 * If we move the `pmt-logger.js` to load earlier the angular modules it refers
 * to has not yet been loaded. When elmInit fires which calls this method we
 * should be able to access PinMeToLogger indirectly.
 *
 * TODO: Slice and dice the build so we can inject the PinMeToLogger in the top scope.
 *
 * @param app Elm application
 */
function registerLogger(app) {
	if (app.ports && app.ports.pinMeToLoggerLog) {
		const logger = window.PinMeToLogger || console;
		app.ports.pinMeToLoggerLog.subscribe(logger.log);
	}
}

function registerRedirectToLoginPage(app) {
	const redirectFunc = () => {
		location.href = window.pmtEnv.urls.login + '?returnUrl=' + encodeURIComponent(location.href);
	};
	app.ports?.main_redirectToLoginPage?.subscribe(redirectFunc);
	app.ports?.posts_redirectToLoginPage?.subscribe(redirectFunc);
}

const elmInit = (moduleName, elmModule, node, flags, elmVersion) => {
	switch (parseFloat(elmVersion)) {
		default:
			const app = flags
				? elmModule.init({ node: node, flags: { moduleName, ...flags } })
				: elmModule.init({ node }, { moduleName });
			registerLogger(app);
			registerRedirectToLoginPage(app);
			window.pmtUi.init(app);

			return app;
	}
};

const elmService = function ($i18next, pmtPlanHatService, pmtEnvService) {
	const runningModules = {};

	function create(div, elmModuleName, args, elmVersion) {
		const parts = elmModuleName.split('.');
		const elmModule = parts.reduce(function (obj, p) {
			if (obj == null) {
				console.log('incorrect ELM module name', elmModuleName);
			}

			return obj[p];
		}, Elm);

		return elmInit(elmModuleName, elmModule, div, args, elmVersion);
	}

	function callPort(port, args) {
		try {
			port.send(args);
		} catch (err) {
			console.log('Failed to call port', { port: port, args: args });
			console.error(err);
		}
	}

	function assertPort(elmModuleName, portName) {
		if (
			runningModules[elmModuleName] &&
			runningModules[elmModuleName].module &&
			runningModules[elmModuleName].module.ports[portName] == null
		) {
			throw `Cannot find portName '${portName}' in module '${elmModuleName}'`;
		}
	}

	const mapPortName = (prefix, id) => {
		if (id === pmtPlanHatService.elmPlanHatPortId) {
			return planHatTrackingPort;
		}

		return `${prefix}_${id}`;
	};

	const _planHatTrackingPortFunction = trackingItem => {
		pmtPlanHatService.track(trackingItem.action, trackingItem.info);
	};

	const _registerSubscription = (elmModuleName, portName, portFunction) => {
		if (this.verifyPortExist(elmModuleName, portName)) {
			assertPort(elmModuleName, portName);
			runningModules[elmModuleName].module.ports[portName].subscribe(portFunction);
			return true;
		}

		return false;
	};

	const _subscriptionFailedLog = (prefix, subscription, elmModuleName, portName) => {
		if (!pmtEnvService.isDev) {
			return;
		}

		const availablePortsByPrefix = Object.keys(runningModules[elmModuleName].module.ports).filter(
			port => port.includes(prefix)
		);

		const availablePortsById = Object.keys(runningModules[elmModuleName].module.ports).filter(
			port => port.includes(subscription.id)
		);

		const error = availablePortsByPrefix.length
			? 'Something is likely wrong with the id of the port in Elm.'
			: 'Something is likely wrong with the prefix of the port in Elm.';

		console.error(`Failed to register subscription. ${error}`, {
			id: subscription.id,
			module: elmModuleName,
			expectedPortName: portName,
			closeMatches: [...availablePortsById, ...availablePortsByPrefix]
		});
	};

	const _deregisterSubscription = (elmModuleName, portName, portFunction) => {
		if (this.verifyPortExist(elmModuleName, portName)) {
			assertPort(elmModuleName, portName);
			runningModules[elmModuleName].module.ports[portName].unsubscribe(portFunction);
		}
	};

	const registerSubscriptions = (elmModuleName, subscriptions) => {
		const uniqueSubscriptionsPrefix = 'out_' + elmModuleName.replace(/\./g, '_');
		if (subscriptions && typeof subscriptions.forEach === 'function') {
			subscriptions.forEach(s => {
				const portName = s.port || mapPortName(uniqueSubscriptionsPrefix, s.id);
				const isPortRegistered = _registerSubscription(elmModuleName, portName, s.f);

				if (!isPortRegistered) {
					_subscriptionFailedLog(uniqueSubscriptionsPrefix, s, elmModuleName, portName);
				}
			});
		}

		_registerSubscription(elmModuleName, planHatTrackingPort, _planHatTrackingPortFunction);
	};

	const deregisterSubscriptions = (elmModuleName, subscriptions) => {
		const uniqueSubscriptionsPrefix = 'out_' + elmModuleName.replace(/\./g, '_');
		if (subscriptions && typeof subscriptions.forEach === 'function') {
			subscriptions.forEach(s => {
				const portName = s.port || mapPortName(uniqueSubscriptionsPrefix, s.id);
				_deregisterSubscription(elmModuleName, portName, s.f);
			});
		}

		_deregisterSubscription(elmModuleName, planHatTrackingPort, _planHatTrackingPortFunction);
	};

	function getTranslation(language) {
		if ($i18next && $i18next.i18n && $i18next.i18n.getDataByLanguage) {
			const lng = language || $i18next.i18n.language || 'en';
			const data = $i18next.i18n.getDataByLanguage(lng);
			if (!data) {
				return {};
			}

			function fixSingular(obj, v, k) {
				if (typeof v !== 'string') {
					Object.keys(v).forEach(x => fixSingular(v, v[x], x));
				} else if (k.endsWith('_0')) {
					obj[k.replace('_0', '')] = obj[k.replace('_0', '')] || v;
				}
			}

			const result = {};
			Object.keys(data).forEach(ns => {
				Object.assign(result, data[ns]);

				fixSingular(data, data[ns], ns);
			});
			return result;
		}
		return {};
	}

	this.getTranslation = getTranslation;

	this.shouldSkipLanguageInfo = function (data, skipLanguageInfo) {
		return !(
			skipLanguageInfo === false &&
			data &&
			typeof data === 'object' &&
			!Array.isArray(data)
		);
	};

	this.sendData = function (toModule, port, data, skipLanguageInfo = false) {
		if (runningModules[toModule]) {
			const m = runningModules[toModule].module;
			if (m.ports[port] == null) {
				console.error(`Port '${port}' does not exist on module '${toModule}'`);
			}
			if (!this.shouldSkipLanguageInfo(data, skipLanguageInfo)) {
				const d = {
					translations: getTranslation(),
					language: $i18next?.i18n?.language || 'en',
					...data
				};
				callPort(m.ports[port], d);
			} else {
				callPort(m.ports[port], data);
			}
		}
	};

	this.verifyPortExist = (elmModuleName, portName) => {
		if (runningModules[elmModuleName]) {
			const m = runningModules[elmModuleName].module;

			return !!(m.ports && m.ports[portName]);
		}

		return false;
	};

	this.checkPorts = function (m, passArgsPort, elmModuleName) {
		if (m.ports[passArgsPort] == null) {
			console.error(`Port '${passArgsPort}' does not exist on module '${elmModuleName}'`);
		}
	};

	this.initialize = function ($element, elmModuleName, args, subscriptions, elmVersion) {
		if (runningModules[elmModuleName]) {
			const m = runningModules[elmModuleName].module;
			const d = runningModules[elmModuleName].div;
			$element.empty();
			$element.append(d);
			if (m.ports) {
				if (m.ports.enableSubscriptions) {
					m.ports.enableSubscriptions.send(null);
				}
				/* Ports are global.
						We have no way to destroy an created ELM app so some ELM apps need arguments to show different data (ex: Details.App, InsightLocks)
					*/
				const moduleUniquePortName = 'passArgs_' + elmModuleName.replace(/\./g, '_');
				const passArgsPort = Object.getOwnPropertyNames(m.ports).find(
					x => x === moduleUniquePortName
				);
				if (passArgsPort && args) {
					this.checkPorts(m, passArgsPort, elmModuleName);

					callPort(m.ports[passArgsPort], args);
				}
			}
			registerSubscriptions(elmModuleName, subscriptions);

			if (m.ports.onInit) {
				this.sendData(elmModuleName, 'onInit', { name: elmModuleName, flags: args });
			}
		} else {
			// Elm replaces its node so we need wrapping div which is removed/appended
			// to the DOM later by this service on next call to initialize.
			// 1. Not wrapping would only work if Elm app has a div as root node
			// 2. This also fixes bug where the view was not rendered after angular-navigation
			const wrapper = $element.children()[0];
			const div = angular.element(wrapper).children()[0];

			runningModules[elmModuleName] = {
				module: create(div, elmModuleName, args, elmVersion),
				args: args,
				div: wrapper
			};
			registerSubscriptions(elmModuleName, subscriptions);
		}
	};

	this.deactivate = function (elmModuleName, subscriptions) {
		const mod = runningModules[elmModuleName];
		if (mod) {
			const m = mod.module;

			if (m.ports && m.ports.disableSubscriptions) {
				m.ports.disableSubscriptions.send(null);
			}
			if (m.ports.onDestroy) {
				this.sendData(elmModuleName, 'onDestroy', { name: elmModuleName });
			}
		}

		deregisterSubscriptions(elmModuleName, subscriptions);
	};
};

angular
	.module('PinMeTo.elm')
	.service('pmtElmService', ['$i18next', 'pmtPlanHatService', 'pmtEnvService', elmService]);
